<script>
import Api from '../api.js';
import FormInput from '~/components/form_input';
import Icon from '~/components/icon';

export default {
  components: {
    FormInput,
    Icon
  },

  props: {
    inputsData: {
      type: Object,
      required: true
    },

    countrySelectOptions: {
      type: Array,
      required: true
    },

    form: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      selectedCountry: this.form.country || this.countrySelectOptions[0][1],
      kvkNumber: this.form.kvkNumber,
      companyName: this.form.companyName,
      street: this.form.street,
      houseNumber: this.form.houseNumber,
      postalCode: this.form.postalCode,
      city: this.form.city,
      profiles: [],
      showDropdown: false,
      dropdownMessage: null
    };
  },

  mounted() {
    this.api = new Api;
  },

  methods: {
    async searchProfiles(companyName) {
      this.companyName = companyName;
      this.profiles = [];

      if (this.countrySupportsKvkNumber() && this.companyNameSearchable()) {
        this.showDropdown = true;
        this.dropdownMessage = this.t('user_backend.js.common.loading');

        try {
          await this.api.searchCompanyProfiles(this.companyName)
            .then ((response) => {
              this.profiles = response.data;

              if (this.profiles.length == 0) {
                this.dropdownMessage = this.t('user_backend.js.sign_up.no_organizations_found');
              } else {
                this.dropdownMessage = null;
              }
            });
        } catch (_) {
          this.dropdownMessage = this.t('user_backend.js.sign_up.no_organizations_found');
        }
      }
    },

    async fillCompanyData(number) {
      this.kvkNumber = number;

      if (this.countrySupportsKvkNumber() && this.kvkNumberIsValid()) {
        try {
          await this.api.getCompanyData(this.kvkNumber)
            .then ((response) => {
              this.companyName = response.data.companyName;
              this.street = response.data.street;
              this.houseNumber = response.data.houseNumber;
              this.postalCode = response.data.postalCode;
              this.city = response.data.city;
              this.hideDropdown();
            });
        } catch { /* empty */ }
      }
    },

    companyNameSearchable() {
      if (this.companyName.length >= 4) {
        return true;
      }
    },

    kvkNumberIsValid() {
      if (this.kvkNumber.length === 8) {
        return true;
      }
    },

    countrySupportsKvkNumber() {
      if (this.selectedCountry == 1) {
        return true;
      }
    },

    hideDropdown() {
      this.showDropdown = false;
    },
  }
};
</script>

<template>
  <div>
    <div class="c-form__group">
      <label
        :for="inputsData['country'].id"
        class="text-nowrap"
      >
        {{ inputsData['country'].label }}
      </label>

      <select
        :id="inputsData['country'].id"
        v-model="selectedCountry"
        :name="inputsData['country'].name"
        class="no-mouseflow"
      >
        <option
          v-for="option in countrySelectOptions"
          :key="option[1]"
          :value="option[1]"
        >
          {{ option[0] }}
        </option>
      </select>

      <div
        v-if="form.errors['country']"
        class="c-form__error"
      >
        {{ form.errors['country'].join() }}
      </div>
    </div>

    <div class="c-form__group position-relative mb-0">
      <button
        v-if="showDropdown"
        class="c-button c-button--link position-absolute end-0"
        :title="t('user_backend.js.sign_up.close_dropdown')"
        @click.prevent="hideDropdown()"
      >
        <icon
          name="x"
          size="header"
        />
      </button>
      <formInput
        :id="inputsData['companyName'].id"
        :name="inputsData['companyName'].name"
        :label="inputsData['companyName'].label"
        :value="companyName"
        :errors="form.errors['companyName']"
        input-class="no-mouseflow"
        @input-event="searchProfiles($event.target.value)"
      />
    </div>

    <div class="position-relative mt-1 mb-3">
      <div
        v-if="showDropdown"
        class="c-signup-page__dropdown w-100"
      >
        <div
          v-for="profile in profiles"
          :key="profile.value"
          class="c-signup-page__dropdown--item px-3 py-2"
          @click="fillCompanyData(profile.kvkNumber)"
        >
          <strong>{{ profile.name }}</strong>
          <div>{{ profile.kvkNumber }}</div>
        </div>
        <div
          v-if="dropdownMessage"
          class="text-center p-2"
        >
          {{ dropdownMessage }}
        </div>
      </div>
    </div>

    <div class="c-form__group row g-3 gx-xl-5">
      <div class="col-md-9">
        <formInput
          :id="inputsData['street'].id"
          :name="inputsData['street'].name"
          :label="inputsData['street'].label"
          :value="street"
          :errors="form.errors['street']"
          input-class="no-mouseflow"
        />
      </div>

      <div class="col-md-3">
        <formInput
          :id="inputsData['houseNumber'].id"
          :name="inputsData['houseNumber'].name"
          :label="inputsData['houseNumber'].label"
          :value="houseNumber"
          :errors="form.errors['houseNumber']"
          input-class="no-mouseflow"
        />
      </div>
    </div>

    <div class="c-form__group row g-3 gx-xl-5">
      <div class="col-md-3">
        <formInput
          :id="inputsData['postalCode'].id"
          :name="inputsData['postalCode'].name"
          :label="inputsData['postalCode'].label"
          :value="postalCode"
          :errors="form.errors['postalCode']"
          input-class="no-mouseflow"
        />
      </div>

      <div class="col-md-9">
        <formInput
          :id="inputsData['city'].id"
          :name="inputsData['city'].name"
          :label="inputsData['city'].label"
          :value="city"
          :errors="form.errors['city']"
          input-class="no-mouseflow"
        />
      </div>
    </div>

    <div class="c-form__group">
      <formInput
        :id="inputsData['kvkNumber'].id"
        :name="inputsData['kvkNumber'].name"
        :label="inputsData['kvkNumber'].label"
        :value="kvkNumber"
        :errors="form.errors['kvkNumber']"
        input-class="no-mouseflow"
        @input-event="fillCompanyData($event.target.value)"
      />
    </div>
  </div>
</template>
